import React from 'react';
import { motion } from 'framer-motion';
import { services } from '../data/services';

const About = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 }
    }
  };

  return (
    <motion.div
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-100px" }}
      variants={containerVariants}
      className="max-w-6xl"
    >
      {/* Header */}
      <motion.div variants={itemVariants} className="mb-12">
        <h2 className="text-3xl font-semibold text-white-1 mb-4">
          About <span className="gradient-text">Me</span>
        </h2>
        <div className="w-12 h-0.5 bg-accent-yellow rounded-full mb-6"></div>
      </motion.div>

      {/* Introduction */}
      <motion.div variants={itemVariants} className="mb-16">
        <div className="max-w-3xl">
          <p className="text-lg text-light-gray/80 leading-relaxed mb-8">
            I'm a passionate web developer and DevOps engineer  
            building scalable applications and robust deployment pipelines. I specialize in 
            modern web technologies and cloud infrastructure.
          </p>
        </div>
      </motion.div>

      {/* Services Section */}
      <motion.div variants={itemVariants}>
        <h3 className="text-2xl font-semibold text-white-1 mb-8">
          What I <span className="gradient-text">Do</span>
        </h3>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {services.map((service, index) => (
            <motion.div
              key={service.id}
              variants={itemVariants}
              whileHover={{ y: -2, scale: 1.01 }}
              transition={{ duration: 0.3 }}
              className="group minimal-card rounded-xl p-6 subtle-glow"
            >
              <div className="text-2xl mb-3 group-hover:scale-105 transition-transform duration-300">
                {service.icon}
              </div>
              
              <h4 className="text-lg font-medium text-white-1 mb-2 group-hover:text-accent-yellow transition-colors duration-300">
                {service.title}
              </h4>
              
              <p className="text-light-gray/80 text-sm mb-4 leading-relaxed">
                {service.description}
              </p>
              
              <div className="flex flex-wrap gap-1">
                {service.technologies.slice(0, 3).map((tech, techIndex) => (
                  <span
                    key={techIndex}
                    className="px-2 py-1 text-xs bg-white-1/10 text-accent-yellow/80 rounded-md"
                  >
                    {tech}
                  </span>
                ))}
                {service.technologies.length > 3 && (
                  <span className="px-2 py-1 text-xs text-light-gray/60">
                    +{service.technologies.length - 3}
                  </span>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Stats Section */}
      <motion.div variants={itemVariants} className="mt-16">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
          {[
            { number: "2+", label: "Years Experience" },
            { number: "15+", label: "Projects Completed" },
            { number: "10+", label: "Technologies" },
            { number: "100%", label: "Client Satisfaction" }
          ].map((stat, index) => (
            <motion.div
              key={index}
              whileHover={{ scale: 1.05 }}
              className="text-center p-6 bg-card-dark/30 border border-jet/30 rounded-xl backdrop-blur-sm"
            >
              <div className="text-3xl lg:text-4xl font-bold gradient-text mb-2">
                {stat.number}
              </div>
              <div className="text-light-gray text-sm lg:text-base">
                {stat.label}
              </div>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default About;
